import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Pencil, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EDIT_STAT_GROUPS } from './gameConstants';
import { sortByJersey, teamKeyOf } from './gameUtils';
import { statsGameApi } from './statsGameApi';

const label = (text) => <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1.5">{text}</div>;

const toggleClass = (on) => (on
  ? 'bg-amber-400 text-black border-amber-400'
  : 'bg-white/5 text-white/70 border-white/20 hover:border-white/60');

const statBtnClass = (selected, tone) => {
  if (!selected) return 'bg-white/5 text-white/80 border-white/20 hover:border-white/60';
  if (tone === 'made') return 'bg-emerald-400 text-black border-emerald-400';
  if (tone === 'miss') return 'bg-rose-400 text-black border-rose-400';
  return 'bg-amber-400 text-black border-amber-400';
};

function TeamSwitch({ team, setTeam, game }) {
  return (
    <div className="mb-3">
      {label('Team')}
      <div className="grid grid-cols-2 gap-2">
        {['a', 'b'].map(t => (
          <button key={t} onClick={() => setTeam(t)}
            className={`h-10 rounded-lg text-sm font-bold border transition-all truncate px-2 ${toggleClass(team === t)}`}
            data-testid={`edit-entry-team-${t}`}>
            {game?.[teamKeyOf(t)]?.name || `Team ${t.toUpperCase()}`}
          </button>
        ))}
      </div>
    </div>
  );
}

function PlayerGrid({ roster, playerId, setPlayerId }) {
  return (
    <div className="mb-3">
      {label('Player')}
      {roster.length === 0 ? (
        <div className="text-white/50 text-sm">No players on this team.</div>
      ) : (
        <div className="grid grid-cols-3 gap-2 max-h-56 overflow-y-auto pr-1">
          {roster.map(p => (
            <button key={p.id} onClick={() => setPlayerId(p.id)}
              className={`rounded-lg p-2 text-left border transition-all ${
                playerId === p.id
                  ? 'bg-emerald-400/15 border-emerald-400 shadow-[0_0_16px_rgba(52,211,153,0.25)]'
                  : 'bg-white/5 border-white/15 hover:border-white/40'
              }`}
              data-testid={`edit-entry-player-${p.id}`}>
              <div className="text-amber-300 text-sm font-black italic">#{p.jersey_number || '?'}</div>
              <div className="text-white text-xs leading-snug font-semibold truncate">{p.name || 'Unknown'}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function StatGrid({ statType, setStatType }) {
  return (
    <div className="mb-4">
      {label('Stat')}
      {EDIT_STAT_GROUPS.map(grp => (
        <div key={grp.label} className="mb-2">
          <div className="text-white/40 text-[10px] font-semibold mb-1">{grp.label}</div>
          <div className="flex flex-wrap gap-1.5">
            {grp.items.map(s => (
              <button key={s.key} onClick={() => setStatType(s.key)}
                className={`h-9 px-3 text-xs font-bold rounded-md border transition-all ${statBtnClass(statType === s.key, s.tone)}`}
                data-testid={`edit-entry-stat-${s.key}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// Tap a feed play to fix the player / team / stat type, or delete it.
export function EditEntryModal({ entry, game, onClose, onSaved }) {
  const [team, setTeam] = useState(entry.team);
  const [playerId, setPlayerId] = useState(entry.player_id);
  const [statType, setStatType] = useState(entry.stat_type);
  const [saving, setSaving] = useState(false);

  const roster = useMemo(
    () => [...(game?.[teamKeyOf(team)]?.players || [])].sort(sortByJersey),
    [team, game],
  );

  // Team flip -> auto-pick the first player so Save never sends a player from the other team.
  useEffect(() => {
    if (roster.length && !roster.some(p => p.id === playerId)) setPlayerId(roster[0].id);
  }, [roster, playerId]);

  const dirty = team !== entry.team || playerId !== entry.player_id || statType !== entry.stat_type;

  const run = async (request, okMsg, errMsg, notify) => {
    setSaving(true);
    try {
      const res = await request();
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || errMsg);
        return;
      }
      notify(okMsg);
      onSaved();
      onClose();
    } catch {
      toast.error(errMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (!dirty) { onClose(); return; }
    run(() => statsGameApi.patchEntry(game.id, entry.id, { team, player_id: playerId, stat_type: statType }),
      'Play updated', 'Could not update play', toast.success);
  };

  const handleDelete = () => run(() => statsGameApi.undo(game.id, entry.id),
    'Play deleted', 'Could not delete play', toast.info);

  return (
    <div className="fixed inset-0 z-[112] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-testid="edit-entry-overlay"
      onClick={onClose}>
      <div
        className="w-full sm:max-w-lg bg-[#001A5C] rounded-t-2xl sm:rounded-2xl border border-white/10 p-4 sm:p-5 max-h-[92vh] overflow-y-auto"
        data-testid="edit-entry-modal"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Pencil className="w-5 h-5 text-amber-400" />
            <div className="text-white text-lg font-bold">Edit play</div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}
            className="text-white/60 hover:text-white hover:bg-white/10"
            data-testid="edit-entry-close">
            <X className="w-4 h-4" />
          </Button>
        </div>
        <TeamSwitch team={team} setTeam={setTeam} game={game} />
        <PlayerGrid roster={roster} playerId={playerId} setPlayerId={setPlayerId} />
        <StatGrid statType={statType} setStatType={setStatType} />
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleDelete} disabled={saving}
            className="border-rose-400/60 text-rose-300 hover:bg-rose-500/10 hover:text-rose-200"
            data-testid="edit-entry-delete">
            Delete
          </Button>
          <div className="flex-1" />
          <Button variant="ghost" onClick={onClose} disabled={saving}
            className="text-white/70 hover:text-white hover:bg-white/10"
            data-testid="edit-entry-cancel">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || !dirty}
            className="bg-amber-400 hover:bg-amber-300 text-black font-black disabled:opacity-40"
            data-testid="edit-entry-save">
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}
